import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Divider, Tooltip } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";
import dayjs from "dayjs";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { Input } from "@/components/@custom/Input";
import { Plate } from "@/components/@custom/Plate";
import { useQueueUpdateCountAttendance } from "@/domain";
import { useSession } from "@/hooks/@custom/useSession";
import { QueueCompany, SaveQueueStartArgs, useSaveQueueStartMutation } from "@/store/apis/nextcargo";
import { SaveSchedulingCloseArgs, useSaveSchedulingCloseMutation } from "@/store/apis/nextcargo/scheduling";

type ShutdownAttendanceSchemaArgs = z.infer<typeof shutdownAttendanceSchema>;
const shutdownAttendanceSchema = z.object({
  qtyTransported: z
    .string({ required_error: "Informe a quantidade" })
    .refine((val) => !isNaN(Number(val.replace(/\D/g, ""))) && Number(val.replace(/\D/g, "")) > 0 && Number.isInteger(Number(val.replace(/\D/g, ""))), {
      message: "O valor precisa ser um número inteiro positivo"
    })
});
function ExpeditionActionsAttendanceShutdownModal({ context, id, innerProps }: ContextModalProps<QueueCompany>) {
  const { expedition } = useSession();
  const companyId = String(expedition?.company.id);

  const [trigger] = useSaveSchedulingCloseMutation();
  const { updateCountAttendance } = useQueueUpdateCountAttendance();

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    setError,
    control,
    reset
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(shutdownAttendanceSchema),
    defaultValues: {
      qtyTransported: "0"
    }
  });

  const onSubmit: SubmitHandler<ShutdownAttendanceSchemaArgs> = async (data) => {
    const { qtyTransported } = data;
    const { scheduling, expedition } = innerProps;

    const qtyTransportedNumber = Number(qtyTransported.replace(",", "."));

    const body: SaveSchedulingCloseArgs = {
      id: scheduling?.id as number,
      status: true,
      qtyTransported: qtyTransportedNumber,
      expedInitialDate: expedition!,
      expedDate: dayjs().toDate()
    };

    /* alert(JSON.stringify(body, null, 2)) */

    // if (qtyTransportedNumber > Number(scheduling?.truck.grossCapacity)) {
    //   setError("qtyTransported", { message: "O peso não pode exceder a capacidade máxima do caminhão!", type: "max" });
    //   return;
    // }

    const { error } = await trigger(body);
    if (!error) {
      updateCountAttendance(+companyId);
      handleDimiss();
    }
  };

  const handleDimiss = () => {
    context.closeModal(id);
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 pt-4">
      <div>
        <VehicleDetails scheduling={innerProps.scheduling} />

        <Divider variant="dashed" color="gray" my="md" />

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:flex-row md:items-center md:justify-center md:gap-6">
          <div className="text-center">
            <p className="text-xl">Cap. Liq. Veículo</p>
            <p className="font-mono text-2xl font-semibold">{innerProps?.scheduling?.qtyOrdered}t</p>
          </div>

          <Input.Text control={control} name="qtyTransported" label="Peso Balança:" placeholder="0" maskProps={{ mask: "99,999" }} autoFocus />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <Button size="sm" radius="sm" color="red" onClick={handleDimiss} disabled={isSubmitting}>
          Cancelar
        </Button>

        <Tooltip label="Nenhuma mudança detectada" disabled={isDirty}>
          <Button size="sm" radius="sm" color="green" type="submit" disabled={!isDirty || !isValid} loading={isSubmitting}>
            Concluir
          </Button>
        </Tooltip>
      </div>
    </form>
  );
}

function ExpeditionActionsAttendanceBackToQueueModal({ context, id, innerProps }: ContextModalProps<QueueCompany>) {
  const [trigger, { isLoading }] = useSaveQueueStartMutation();
  const { updateCountAttendance } = useQueueUpdateCountAttendance();

  const handleDimiss = () => {
    context.closeModal(id);
  };

  const handleAccept = async () => {
    const innerId = +innerProps.id;

    const body: SaveQueueStartArgs = {
      id: innerId,
      status: false
    };
    try {
      await trigger(body);
      updateCountAttendance(innerId);
      handleDimiss();
    } catch {
      // Do nothing
    }
  };

  return (
    <div className="flex flex-col gap-4 pt-4">
      <VehicleDetails scheduling={innerProps.scheduling} />

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <Button size="sm" radius="sm" color="red" onClick={handleDimiss} disabled={isLoading}>
          Não
        </Button>

        <Button size="sm" radius="sm" color="green" onClick={handleAccept} loading={isLoading}>
          Sim
        </Button>
      </div>
    </div>
  );
}

function VehicleDetails({ scheduling }: { scheduling: QueueCompany["scheduling"] }) {
  const truck = scheduling?.truck;

  return (
    <div className="flex items-center gap-2 text-red-500">
      {truck?.licensePlate && <Plate plate={truck.licensePlate} color="text-current" />}

      <div className="flex flex-col gap-1 leading-none text-current">
        <p>{truck?.vehicleModel.name}</p>
        <p>
          {truck?.implementType} - {truck?.netCapacity}t
        </p>
      </div>
    </div>
  );
}

export const ExpeditionActionsAttendance = {
  shutdown: ExpeditionActionsAttendanceShutdownModal,
  backToQueue: ExpeditionActionsAttendanceBackToQueueModal
};
